import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Droplets } from 'lucide-react';
import * as api from '../services/api';

/**
 * AuthCallback - Handles Google OAuth redirect
 * Processes session_id from URL fragment and exchanges it for user session
 */
const AuthCallback = ({ onLogin }) => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL hash
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error('No session_id found in URL');
          navigate('/', { replace: true });
          return;
        }

        const sessionId = sessionIdMatch[1];
        
        // Exchange session_id for user data
        const result = await api.processGoogleSession(sessionId);
        
        if (result.success && result.user) {
          // Notify parent of login and navigate to dashboard
          if (onLogin) {
            onLogin(result.user);
          }
          navigate('/', { replace: true, state: { user: result.user } });
        } else {
          throw new Error('Invalid response from auth server');
        }
      } catch (error) {
        console.error('Google auth failed:', error);
        // Clear any partial state and redirect to login
        api.clearAuth();
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [navigate, onLogin]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 flex items-center justify-center">
      <div className="text-center">
        <img src="/icons/icon-180.png" alt="StreakFree" className="inline-block w-16 h-16 rounded-2xl mb-6 shadow-lg shadow-emerald-500/30" />
        <div className="flex items-center justify-center gap-3 mb-4">
          <Loader2 size={24} className="text-emerald-400 animate-spin" />
          <span className="text-white text-lg font-medium">Signing you in...</span>
        </div>
        <p className="text-zinc-400 text-sm">Please wait while we verify your account</p>
      </div>
    </div>
  );
};

export default AuthCallback;
